import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  ApiError,
  api,
  type MType,
  type Profession,
  type SelfReportSummary,
  type SelfReportUpcomingEvent,
} from '@/lib/api';
import PlayerHubHeader from '@/components/PlayerHubHeader';
import ReportingBanner from '@/components/ReportingBanner';
import ChatBanCard from '@/components/ChatBanCard';
import { usePwaManifest } from './PlayerHubMe';

const M_TYPES: MType[] = ['planes', 'rockets', 'tanks'];

interface DraftValues {
  m1Power: string;
  m1Type: MType;
  m2Power: string;
  m2Type: MType;
  m3Power: string;
  m3Type: MType;
}

const emptyDraft = (): DraftValues => ({
  m1Power: '',
  m1Type: 'planes',
  m2Power: '',
  m2Type: 'rockets',
  m3Power: '',
  m3Type: 'tanks',
});

function seedFrom(summary: SelfReportSummary): DraftValues {
  const source = summary.pending ?? summary.latestApproved;
  if (!source) return emptyDraft();
  return {
    m1Power: source.m1Power ? source.m1Power.toFixed(2) : '',
    m1Type: source.m1Type,
    m2Power: source.m2Power ? source.m2Power.toFixed(2) : '',
    m2Type: source.m2Type,
    m3Power: source.m3Power ? source.m3Power.toFixed(2) : '',
    m3Type: source.m3Type,
  };
}

function parsePower(raw: string): number | null {
  const cleaned = raw.trim().replace(',', '.');
  if (cleaned === '') return null;
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

export default function SelfReport() {
  const params = useParams<{ token: string }>();
  const token = params.token ?? '';
  const qc = useQueryClient();
  const summaryQuery = useQuery({
    queryKey: ['self-report', token],
    queryFn: () => api.getSelfReportSummary(token),
    retry: false,
    enabled: !!token,
  });

  const [draft, setDraft] = useState<DraftValues>(emptyDraft);
  const [seeded, setSeeded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [savedAutoApproved, setSavedAutoApproved] = useState(false);
  const summaryPlayer = summaryQuery.data?.player;
  // Report is the hub's landing page, so "Add to home screen" happens here.
  usePwaManifest(
    token,
    summaryPlayer ? summaryPlayer.displayName?.trim() || summaryPlayer.name : '',
  );

  useEffect(() => {
    if (!seeded && summaryQuery.data) {
      setDraft(seedFrom(summaryQuery.data));
      setSeeded(true);
    }
  }, [summaryQuery.data, seeded]);

  const submit = useMutation({
    mutationFn: () => {
      const m1 = parsePower(draft.m1Power);
      const m2 = parsePower(draft.m2Power);
      const m3 = parsePower(draft.m3Power);
      if (m1 == null || m2 == null || m3 == null) {
        throw new ApiError(
          400,
          'Please enter all three values in millions with up to 2 decimals (e.g. 12.34).',
        );
      }
      return api.submitSelfReport(token, {
        m1Power: m1,
        m1Type: draft.m1Type,
        m2Power: m2,
        m2Type: draft.m2Type,
        m3Power: m3,
        m3Type: draft.m3Type,
      });
    },
    onSuccess: (res) => {
      setSavedAt(res.submittedAt);
      setSavedAutoApproved(res.autoApproved);
      setError(null);
      qc.invalidateQueries({ queryKey: ['self-report', token] });
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Failed to submit'),
  });

  const mSum = useMemo(() => {
    const m1 = parsePower(draft.m1Power) ?? 0;
    const m2 = parsePower(draft.m2Power) ?? 0;
    const m3 = parsePower(draft.m3Power) ?? 0;
    return m1 + m2 + m3;
  }, [draft]);

  if (summaryQuery.isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        <div className="mx-auto max-w-md px-4 py-10 text-slate-400">Loading…</div>
      </div>
    );
  }
  if (summaryQuery.isError || !summaryQuery.data) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        <div className="mx-auto max-w-md px-4 py-10">
          <h1 className="mb-2 text-xl font-semibold">Link not valid</h1>
          <p className="text-sm text-slate-400">
            This self-report link is invalid or has been revoked. Please ask an
            R4/R5 to send you a fresh one.
          </p>
        </div>
      </div>
    );
  }

  const summary = summaryQuery.data;
  const { player, activeRequest, latestApproved, pending, history } = summary;
  const previousSum = latestApproved
    ? latestApproved.m1Power + latestApproved.m2Power + latestApproved.m3Power
    : null;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <PlayerHubHeader
        token={token}
        playerLabel={player.displayName?.trim() || player.name}
        playerRank={player.rank}
        subtitle="Strength self-report"
        upcomingEvents={summary.upcomingEvents}
      />

      <main className="mx-auto max-w-md space-y-4 px-4 py-6">
        <ChatBanCard token={token} />
        <ReportingBanner token={token} />

        {activeRequest && (
          <div className="rounded-lg border border-indigo-800/70 bg-indigo-900/30 px-4 py-3 text-sm">
            <div className="font-medium text-indigo-100">Strength Request active</div>
            <div className="mt-0.5 text-xs text-indigo-200/80">
              "{activeRequest.title}" is currently open — please submit your
              latest values.
            </div>
          </div>
        )}

        {latestApproved && (
          <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-3 text-xs text-slate-400">
            <div>
              Last confirmed on{' '}
              <span className="text-slate-200">
                {latestApproved.recordedAt.slice(0, 10)}
              </span>
              :{' '}
              <span className="tabular-nums text-slate-200">
                {latestApproved.m1Power.toFixed(2)} /{' '}
                {latestApproved.m2Power.toFixed(2)} /{' '}
                {latestApproved.m3Power.toFixed(2)}
              </span>
              <span className="ml-1 text-slate-500">
                (Σ {previousSum?.toFixed(2)})
              </span>
            </div>
          </div>
        )}

        {pending && !savedAt && (
          <div className="rounded-lg border border-amber-800/70 bg-amber-900/20 px-4 py-3 text-xs text-amber-200">
            You have a submission waiting for admin review, submitted{' '}
            {pending.submittedAt.slice(0, 16).replace('T', ' ')}. You can
            re-submit — the newest values replace the pending ones.
          </div>
        )}

        {savedAt && (
          <div className="rounded-lg border border-emerald-800/70 bg-emerald-900/20 px-4 py-3 text-sm text-emerald-200">
            {savedAutoApproved ? (
              <>
                Saved directly on{' '}
                {savedAt.slice(0, 16).replace('T', ' ')} — your values are now
                live in the roster.
              </>
            ) : (
              <>
                Thanks — your values were submitted on{' '}
                {savedAt.slice(0, 16).replace('T', ' ')} and are waiting for
                admin approval.
              </>
            )}
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            setSavedAt(null);
            submit.mutate();
          }}
          className="space-y-4 rounded-lg border border-slate-800 bg-slate-900/40 p-4"
        >
          <p className="text-xs text-slate-400">
            Enter values in <span className="text-slate-200">millions</span>{' '}
            with up to 2 decimals — e.g. <code>12.34</code>.
          </p>

          {(['m1', 'm2', 'm3'] as const).map((key) => {
            const powerKey = `${key}Power` as const;
            const typeKey = `${key}Type` as const;
            return (
              <div key={key} className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 text-xs font-medium uppercase tracking-wider text-slate-400">
                    {key.toUpperCase()}
                  </div>
                  <div className="relative flex-1">
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={draft[powerKey]}
                      onChange={(e) =>
                        setDraft((d) => ({
                          ...d,
                          [powerKey]: e.target.value.replace(',', '.'),
                        }))
                      }
                      style={{
                        color: '#e2e8f0',
                        WebkitTextFillColor: '#e2e8f0',
                        caretColor: '#e2e8f0',
                        backgroundColor: '#020617',
                        fontSize: '16px',
                      }}
                      className="w-full rounded border border-slate-600 px-3 py-2.5 pr-12 text-right"
                    />
                    <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-slate-500">
                      Mio
                    </span>
                  </div>
                </div>
                <div className="flex gap-1 pl-10">
                  {M_TYPES.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() =>
                        setDraft((d) => ({ ...d, [typeKey]: t }))
                      }
                      className={
                        'flex-1 rounded border px-2 py-1.5 text-xs capitalize ' +
                        (draft[typeKey] === t
                          ? 'border-indigo-500 bg-indigo-600/30 text-indigo-100'
                          : 'border-slate-700 bg-slate-950 text-slate-400')
                      }
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}

          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>
              M-Sum:{' '}
              <span className="tabular-nums text-slate-300">
                {mSum.toFixed(2)}
              </span>
            </span>
            {previousSum != null && (
              <span>
                Δ vs. last:{' '}
                <span
                  className={
                    'tabular-nums ' +
                    (mSum - previousSum >= 0
                      ? 'text-emerald-300'
                      : 'text-amber-300')
                  }
                >
                  {(mSum - previousSum).toFixed(2)}
                </span>
              </span>
            )}
          </div>

          {error && <div className="text-sm text-red-400">{error}</div>}

          <button
            type="submit"
            disabled={submit.isPending}
            className="w-full rounded bg-indigo-600 py-2.5 text-sm font-medium hover:bg-indigo-500 disabled:opacity-50"
          >
            {submit.isPending ? 'Submitting…' : 'Submit for review'}
          </button>

          <p className="text-[11px] text-slate-500">
            An R4/R5 admin will review and approve your entry before it counts
            in the roster.
          </p>
        </form>

        <KillsSection
          token={token}
          latestKills={summary.latestKills}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['self-report', token] });
          }}
        />

        <ProfessionSection token={token} profession={player.profession} />

        {(history.length > 0 || summary.pendingList.length > 0) && (
          <HistorySection rows={history} pending={summary.pendingList} />
        )}
      </main>
    </div>
  );
}

type HistoryEntry = {
  key: string;
  recordedAt: string;
  m1Power: number;
  m1Type: MType;
  m2Power: number;
  m2Type: MType;
  m3Power: number;
  m3Type: MType;
  source: string | null;
  pending: boolean;
};

function HistorySection({
  rows,
  pending,
}: {
  rows: SelfReportSummary['history'];
  pending: SelfReportSummary['pendingList'];
}) {
  const merged: HistoryEntry[] = [
    ...pending.map((p) => ({
      key: `p${p.id}`,
      recordedAt: p.submittedAt,
      m1Power: p.m1Power,
      m1Type: p.m1Type,
      m2Power: p.m2Power,
      m2Type: p.m2Type,
      m3Power: p.m3Power,
      m3Type: p.m3Type,
      source: null,
      pending: true,
    })),
    ...rows.map((r) => ({
      key: `h${r.id}`,
      recordedAt: r.recordedAt,
      m1Power: r.m1Power,
      m1Type: r.m1Type,
      m2Power: r.m2Power,
      m2Type: r.m2Type,
      m3Power: r.m3Power,
      m3Type: r.m3Type,
      source: r.source,
      pending: false,
    })),
  ].sort((a, b) => (a.recordedAt > b.recordedAt ? -1 : 1));

  const filtered = merged.filter((r, i) => {
    const older = merged[i + 1];
    if (!older) return true;
    if (r.pending || older.pending) return true;
    return (
      older.m1Power !== r.m1Power ||
      older.m2Power !== r.m2Power ||
      older.m3Power !== r.m3Power
    );
  });
  return (
    <section className="space-y-2">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
        Your last submissions
      </h2>
      <ul className="space-y-1">
        {filtered.slice(0, 8).map((r, idx) => {
          const sum = r.m1Power + r.m2Power + r.m3Power;
          const prev = filtered[idx + 1] ?? null;
          const prevSum = prev
            ? prev.m1Power + prev.m2Power + prev.m3Power
            : null;
          return (
            <li
              key={r.key}
              className={
                'rounded border px-3 py-2 text-xs ' +
                (r.pending
                  ? 'border-amber-800/60 bg-amber-950/30'
                  : 'border-slate-800 bg-slate-900/40')
              }
            >
              <div className="flex items-center justify-between gap-2">
                <span className="tabular-nums text-slate-400">
                  {r.recordedAt.slice(0, 10)}
                </span>
                {r.pending ? (
                  <span className="rounded bg-amber-900/60 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-200">
                    Awaiting review
                  </span>
                ) : (
                  <SourceTag source={r.source} />
                )}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 tabular-nums text-slate-200">
                <MHistoryCell
                  label="M1"
                  power={r.m1Power}
                  type={r.m1Type}
                  prev={prev?.m1Power ?? null}
                />
                <MHistoryCell
                  label="M2"
                  power={r.m2Power}
                  type={r.m2Type}
                  prev={prev?.m2Power ?? null}
                />
                <MHistoryCell
                  label="M3"
                  power={r.m3Power}
                  type={r.m3Type}
                  prev={prev?.m3Power ?? null}
                />
                <span className="ml-auto text-slate-400">
                  Σ <span className="text-slate-100">{sum.toFixed(2)}</span>
                  {prevSum != null && <DeltaChip delta={sum - prevSum} />}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function MHistoryCell({
  label,
  power,
  type,
  prev,
}: {
  label: string;
  power: number;
  type: MType;
  prev: number | null;
}) {
  return (
    <span className="flex items-baseline gap-1">
      <span className="text-slate-400">{label}</span>
      <span className="text-slate-100">{power.toFixed(2)}</span>
      <span className="text-[10px] text-slate-500">{type}</span>
      {prev != null && <DeltaChip delta={power - prev} />}
    </span>
  );
}

function DeltaChip({ delta }: { delta: number }) {
  const rounded = Math.round(delta * 100) / 100;
  if (rounded === 0)
    return <span className="text-[10px] text-slate-600">±0</span>;
  const positive = rounded > 0;
  return (
    <span
      className={
        'text-[10px] tabular-nums ' +
        (positive ? 'text-emerald-300' : 'text-amber-300')
      }
    >
      {positive ? '+' : ''}
      {rounded.toFixed(2)}
    </span>
  );
}


function SourceTag({ source }: { source: string | null }) {
  if (source === 'self_report')
    return (
      <span className="rounded bg-indigo-900/60 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-indigo-200">
        self-report
      </span>
    );
  if (source === 'strength_request')
    return (
      <span className="rounded bg-emerald-900/60 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-emerald-200">
        strength req.
      </span>
    );
  if (source === 'lastrank')
    return (
      <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-slate-400">
        lastrank
      </span>
    );
  if (source === 'video_import_kills')
    return (
      <span className="rounded bg-rose-900/60 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-rose-200">
        video kills
      </span>
    );
  if (source === 'video_import_power')
    return (
      <span className="rounded bg-sky-900/60 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-sky-200">
        video power
      </span>
    );
  if (source === 'self_report_kills')
    return (
      <span className="rounded bg-red-900/60 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-red-200">
        self-report kills
      </span>
    );
  return (
    <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-slate-400">
      {source ?? 'manual'}
    </span>
  );
}

function UpcomingEventTiles({ events }: { events: SelfReportUpcomingEvent[] }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className={events.length === 1 ? 'grid grid-cols-1 gap-2' : 'grid grid-cols-2 gap-2'}>
      {events.map((e) => (
        <UpcomingEventTile key={e.eventId} event={e} now={now} />
      ))}
    </div>
  );
}

function eventLabel(type: SelfReportUpcomingEvent['type']): string {
  return type === 'canyon_storm' ? 'CS' : 'DS';
}

function UpcomingEventTile({
  event,
  now,
}: {
  event: SelfReportUpcomingEvent;
  now: number;
}) {
  const startsMs = new Date(event.startsAtUtc).getTime();
  const diff = Math.max(0, startsMs - now);
  const started = startsMs - now <= 0;
  const roleLabel = event.role
    ? event.role === 'starter'
      ? 'Starter'
      : 'Sub'
    : 'Assigned';
  const heading = `${roleLabel} ${eventLabel(event.type)} ${event.slot}`;
  return (
    <div
      className={
        'rounded-lg border p-3 ' +
        (event.role === 'starter'
          ? 'border-emerald-800/70 bg-emerald-900/20'
          : 'border-indigo-800/70 bg-indigo-900/20')
      }
    >
      <div className="text-[11px] uppercase tracking-wider text-slate-400">
        {event.eventDate}
      </div>
      <div
        className={
          'text-sm font-semibold ' +
          (event.role === 'starter' ? 'text-emerald-100' : 'text-indigo-100')
        }
      >
        {heading}
      </div>
      <div className="mt-0.5 text-xs text-slate-400">
        {event.startsAtServer} ST
      </div>
      <div className="mt-1.5 font-mono text-lg tabular-nums text-slate-100">
        {started ? 'live now' : formatCountdown(diff)}
      </div>
    </div>
  );
}

function formatCountdown(ms: number): string {
  const total = Math.floor(ms / 1000);
  const days = Math.floor(total / 86_400);
  const hours = Math.floor((total % 86_400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
  if (days > 0) return `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}


function parseKills(raw: string): number | null {
  const cleaned = raw.trim().replace(',', '.');
  if (cleaned === '') return null;
  if (!/^\d+(\.\d{1,3})?$/.test(cleaned)) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

export function formatKills(n: number): string {
  // Up to 3 decimals, trailing zeros trimmed but always at least 1 decimal.
  const s = n.toFixed(3).replace(/0+$/, '').replace(/\.$/, '.0');
  return s;
}

function KillsSection({
  token,
  latestKills,
  onSaved,
}: {
  token: string;
  latestKills: SelfReportSummary['latestKills'];
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<string>(
    latestKills?.kills != null && latestKills.kills > 0
      ? formatKills(latestKills.kills)
      : '',
  );
  const [seeded, setSeeded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    if (seeded) return;
    if (latestKills?.kills != null && latestKills.kills > 0) {
      setDraft(formatKills(latestKills.kills));
    }
    setSeeded(true);
  }, [latestKills, seeded]);

  const submit = useMutation({
    mutationFn: () => {
      const v = parseKills(draft);
      if (v == null) {
        throw new ApiError(
          400,
          'Please enter the kill count in millions with up to 3 decimals (e.g. 128.425).',
        );
      }
      return api.submitSelfReportKills(token, v);
    },
    onSuccess: (res) => {
      setSavedAt(res.submittedAt);
      setError(null);
      onSaved();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Failed to save'),
  });

  const parsedDraft = parseKills(draft);
  const previousKills = latestKills?.kills ?? null;
  const delta =
    parsedDraft != null && previousKills != null
      ? parsedDraft - previousKills
      : null;

  return (
    <section className="space-y-2">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
        Kill count
      </h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          setSavedAt(null);
          submit.mutate();
        }}
        className="space-y-3 rounded-lg border border-rose-900/40 bg-rose-950/20 p-4"
      >
        <p className="text-xs text-rose-100/80">
          Enter your total kill count in{' '}
          <span className="text-rose-50">millions</span> with up to 3 decimals
          — e.g. <code>128.425</code>. Kills update instantly, no admin review.
        </p>
        {previousKills != null && previousKills > 0 && (
          <div className="text-[11px] text-slate-400">
            Last recorded:{' '}
            <span className="tabular-nums text-slate-200">
              {formatKills(previousKills)} M
            </span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <div className="w-14 text-xs font-medium uppercase tracking-wider text-rose-200">
            Kills
          </div>
          <div className="relative flex-1">
            <input
              type="text"
              inputMode="decimal"
              placeholder="0.0"
              value={draft}
              onChange={(e) =>
                setDraft(e.target.value.replace(',', '.'))
              }
              style={{
                color: '#e2e8f0',
                WebkitTextFillColor: '#e2e8f0',
                caretColor: '#e2e8f0',
                backgroundColor: '#020617',
                fontSize: '16px',
              }}
              className="w-full rounded border border-rose-800/60 px-3 py-2.5 pr-12 text-right"
            />
            <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-slate-500">
              Mio
            </span>
          </div>
        </div>
        {delta != null && (
          <div className="text-[11px] text-slate-400">
            Δ vs. last:{' '}
            <span
              className={
                'tabular-nums ' +
                (delta > 0
                  ? 'text-emerald-300'
                  : delta < 0
                    ? 'text-amber-300'
                    : 'text-slate-500')
              }
            >
              {delta > 0 ? '+' : delta < 0 ? '-' : ''}
              {formatKills(Math.abs(delta))} M
            </span>
          </div>
        )}
        {savedAt && (
          <div className="rounded border border-emerald-800/70 bg-emerald-900/20 px-3 py-2 text-xs text-emerald-200">
            Kill count updated on {savedAt.slice(0, 16).replace('T', ' ')}.
          </div>
        )}
        {error && <div className="text-sm text-red-400">{error}</div>}
        <button
          type="submit"
          disabled={submit.isPending || parsedDraft == null}
          className="w-full rounded bg-rose-600 py-2.5 text-sm font-medium hover:bg-rose-500 disabled:opacity-50"
        >
          {submit.isPending ? 'Saving…' : 'Update kills'}
        </button>
      </form>
    </section>
  );
}

const PROFESSION_OPTIONS: { value: Profession; label: string }[] = [
  { value: 'warlord', label: 'Warlord' },
  { value: 'engineer', label: 'Engineer' },
];

function ProfessionSection({
  token,
  profession,
}: {
  token: string;
  profession: Profession | null;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (next: Profession | null) =>
      api.updateSelfReportProfile(token, { profession: next }),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['self-report', token] });
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Failed to save'),
  });
  return (
    <section className="space-y-2">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
        Profession
      </h2>
      <div className="space-y-2 rounded-lg border border-slate-800 bg-slate-900/40 p-4">
        <div className="flex gap-1">
          {PROFESSION_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              disabled={save.isPending}
              onClick={() =>
                save.mutate(profession === o.value ? null : o.value)
              }
              className={
                'flex-1 rounded border px-2 py-2 text-sm disabled:opacity-50 ' +
                (profession === o.value
                  ? 'border-indigo-500 bg-indigo-600/30 text-indigo-100'
                  : 'border-slate-700 bg-slate-950 text-slate-400')
              }
            >
              {o.label}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-slate-500">
          {profession
            ? 'Saved instantly. Tap again to clear.'
            : 'Not set yet — tap your profession. Saved instantly.'}
        </p>
        {error && <div className="text-sm text-red-400">{error}</div>}
      </div>
    </section>
  );
}
